import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Form, Input, Modal, Table, message, Tooltip } from "antd";
import {
  PlusOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  DollarOutlined,
  ShopOutlined,
  TeamOutlined,
  RiseOutlined,
} from "@ant-design/icons";
import { apiClient } from "../../../shared/api/AllApi";
import { formatCurrency, toApiNumber } from "../../../utils/formatters";
import { fetchSystemConfig, readCachedSystemConfig } from "../../../shared/lib/systemConfig";
import { createRequestGuard, isAbortError } from "../../../shared/lib/request";
import { ListPage, StatCard, T } from "../../../shared/ui/PageShell";
import { fixedStart } from "../../../i18";
import usePermissions from "../../../hooks/usePermissions";
import { vendorFinanceFromConsignment } from "../lib/vendorFinance";
import useTablePagination from "../../../shared/ui/useTablePagination";


const Vendors = () => {
  const { t, i18n } = useTranslation();
  const { pagination, resetPage } = useTablePagination();
  const { hasPermission } = usePermissions();
  const canAdd = hasPermission("vendors.add");
  const canEdit = hasPermission("vendors.edit");
  const canDelete = hasPermission("vendors.delete");
  const canFinance = hasPermission("vendors.finance");
  const canIncreaseOwed = hasPermission("maintenance.reset");
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const [systemConfig, setSystemConfig] = useState(() => readCachedSystemConfig());
  const guardRef = useRef(createRequestGuard());

  const mainCode = systemConfig?.currency_code || "IQD";

  const load = useCallback(async () => {
    const req = guardRef.current.next();
    setLoading(true);
    try {
      const cfg = await fetchSystemConfig();
      if (!req.isCurrent()) return;
      setSystemConfig(cfg);
      const res = await apiClient.get("/vendors", { signal: req.signal });
      if (!req.isCurrent()) return;
      const vendors = Array.isArray(res.data) ? res.data : [];

      // Source every finance figure from the same consignment endpoint the
      // vendor detail page uses, so the list totals always match the profile.
      const metricResults = await Promise.allSettled(
        vendors.map((vendor) =>
          apiClient.get(`/pos/vendor-consignment/${vendor.id}`, {
            signal: req.signal,
          })
        )
      );
      if (!req.isCurrent()) return;
      const metricsById = new Map(
        vendors.map((vendor, index) => {
          const result = metricResults[index];
          const data = result?.status === "fulfilled" ? result.value?.data : null;
          const products = Array.isArray(data?.products) ? data.products : [];
          const physical = products.reduce((totals, product) => ({
            net_qty_sold: totals.net_qty_sold + toApiNumber(product.cases_sold, 0),
            net_weight_sold: totals.net_weight_sold + toApiNumber(product.kg_sold, 0),
          }), { net_qty_sold: 0, net_weight_sold: 0 });
          const finance = vendorFinanceFromConsignment(data || {});
          return [Number(vendor.id), {
            ...physical,
            // Product totals only — commission is never part of farmer payback.
            gross: finance.gross,
            expenses: finance.expenses,
            net_owed: finance.netOwed,
            paid: finance.paid,
            remaining: data
              ? finance.remaining
              : toApiNumber(vendor.consignment_owed, 0),
          }];
        })
      );

      setRows(vendors.map((vendor) => ({
        ...vendor,
        ...(metricsById.get(Number(vendor.id)) || {}),
        id: vendor.id,
        vendor_id: vendor.id,
      })));
    } catch (e) {
      if (isAbortError(e)) return;
      if (req.isCurrent()) {
        message.error(e?.response?.data?.message || t("vendorsLoadFailed", "Failed to load."));
        setRows([]);
      }
    } finally {
      if (req.isCurrent()) setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
    return () => guardRef.current.abort();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      name: record.name,
      phone: record.phone || "",
      notes: record.notes || "",
    });
    setModalOpen(true);
  };

  const submit = async () => {
    try {
      const values = await form.validateFields();
      if (editing) {
        await apiClient.put(`/vendors/${editing.id}`, values);
        message.success(t("vendorsUpdated", "Vendor updated."));
      } else {
        await apiClient.post("/vendors", values);
        message.success(t("vendorsCreated", "Vendor added."));
      }
      setModalOpen(false);
      load();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || t("vendorsSaveFailed", "Save failed."));
    }
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: t("vendorsDeleteConfirm", "Delete this vendor? Only if no purchase orders exist."),
      okType: "danger",
      onOk: async () => {
        try {
          await apiClient.delete(`/vendors/${record.id}`);
          message.success(t("vendorsDeleted", "Vendor deleted."));
          load();
        } catch (e) {
          message.error(e?.response?.data?.message || t("vendorsDeleteFailed", "Delete failed."));
        }
      },
    });
  };

  const columns = useMemo(
    () => [
      {
        title: <b>{t("actions", "Actions")}</b>,
        key: "actions",
        align: "center",
        width: 190,
        fixed: fixedStart(i18n.language),
        render: (_, record) => (
          <div style={{ display: "flex", gap: 6, justifyContent: "center", flexWrap: "wrap" }}>
            <Tooltip title={t("view", "View")}>
              <Link to={`/vendors/${record.id}`}>
                <Button size="small" icon={<EyeOutlined />} style={{ borderRadius: 7, background: "#f1f5f9", borderColor: "#e2e8f0" }} />
              </Link>
            </Tooltip>
            {canIncreaseOwed && (
              <Tooltip title={t("increaseFarmerOwedTitle", "Increase Amount Owed to Farmer")}>
                <Link to={`/vendors/${record.id}/increase-owed`}>
                  <Button
                    size="small"
                    icon={<RiseOutlined />}
                    style={{ borderRadius: 7, background: "#fff7ed", borderColor: "#fed7aa", color: "#c2410c" }}
                  />
                </Link>
              </Tooltip>
            )}
            {canEdit && (
              <Tooltip title={t("edit", "Edit")}>
                <Button type="primary" size="small" icon={<EditOutlined />} style={{ borderRadius: 7 }} onClick={() => openEdit(record)} />
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title={t("delete", "Delete")}>
                <Button danger size="small" icon={<DeleteOutlined />} style={{ borderRadius: 7 }} onClick={() => handleDelete(record)} />
              </Tooltip>
            )}
          </div>
        ),
      },
      { title: "#", key: "index", width: 50, render: (_, __, i) => <span style={{ color: T.muted, fontSize: 13 }}>{i + 1}</span> },
      {
        title: <b>{t("vendorsColName", "Name")}</b>, dataIndex: "name", key: "name",
        render: (text, record) => (
          <Link to={`/vendors/${record.id}`} style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
            <div style={{ width: 34, height: 34, borderRadius: "50%", background: "#fef3c7", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <ShopOutlined style={{ color: "#d97706", fontSize: 14 }} />
            </div>
            <div>
              <div style={{ fontWeight: 600, color: T.text, fontSize: 14 }}>{text}</div>
              {record.phone && <div style={{ fontSize: 12, color: T.muted }}>{record.phone}</div>}
            </div>
          </Link>
        ),
      },
      canFinance && {
        title: <b>{t("vendorGross", "Gross")}</b>,
        dataIndex: "gross",
        key: "gross",
        align: "right",
        render: (v) => <span style={{ color: T.sub }}>{formatCurrency(v, mainCode)}</span>,
      },
      // Commission is intentionally hidden from the Vendors table.
      canFinance && {
        title: <b>{t("batchExpenses", "Expenses")}</b>,
        dataIndex: "expenses",
        key: "expenses",
        align: "right",
        render: (v) => <span style={{ color: "#d97706" }}>{formatCurrency(v, mainCode)}</span>,
      },
      canFinance && {
        title: <b>{t("batchNetOwed", "Net owed")}</b>,
        dataIndex: "net_owed",
        key: "net_owed",
        align: "right",
        render: (v) => <span style={{ color: "#0891b2" }}>{formatCurrency(v, mainCode)}</span>,
      },
      canFinance && {
        title: <b>{t("vendorAlreadyPaid", "Paid")}</b>,
        dataIndex: "paid",
        key: "paid",
        align: "right",
        render: (v) => <span style={{ color: T.green }}>{formatCurrency(v, mainCode)}</span>,
      },
      {
        title: <b>{t("vendorRemainingToClear", "Remaining to clear")}</b>,
        dataIndex: canFinance ? "remaining" : "consignment_owed",
        key: "remaining",
        align: canFinance ? "right" : "left",
        render: (v) => {
          const n = toApiNumber(v, 0);
          return <span style={{ fontWeight: 700, color: n > 0 ? T.red : T.green }}>{formatCurrency(n, mainCode)}</span>;
        },
      },
    ].filter(Boolean),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mainCode, t, canEdit, canDelete, canFinance, canIncreaseOwed]
  );

  const filteredRows = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    const visible = term
      ? rows.filter((r) =>
        String(r.name || "").toLowerCase().includes(term) ||
        String(r.phone || "").toLowerCase().includes(term) ||
        String(r.email || "").toLowerCase().includes(term)
      )
      : rows;
    return [...visible].sort((a, b) =>
      toApiNumber(canFinance ? b.remaining : b.consignment_owed, 0)
      - toApiNumber(canFinance ? a.remaining : a.consignment_owed, 0)
    );
  }, [rows, searchText, canFinance]);

  const totals = rows.reduce((acc, r) => ({
    gross: acc.gross + toApiNumber(r.gross, 0),
    netQtySold: acc.netQtySold + toApiNumber(r.net_qty_sold, 0),
    netWeightSold: acc.netWeightSold + toApiNumber(r.net_weight_sold, 0),
    remaining: acc.remaining + toApiNumber(
      canFinance ? r.remaining : r.consignment_owed,
      0
    ),
  }), { gross: 0, netQtySold: 0, netWeightSold: 0, remaining: 0 });
  const debtors = rows.filter((r) =>
    toApiNumber(canFinance ? r.remaining : r.consignment_owed, 0) > 0
  ).length;
  const vendorStats = canFinance
    ? [
      <StatCard key="total" icon={<TeamOutlined style={{ color: "#2563eb" }} />} label={t("vendorsTitle", "Vendors")} value={rows.length} iconBg="#dbeafe" />,
      <StatCard key="net-qty" icon={<ShopOutlined style={{ color: "#2563eb" }} />} label={t("vendorNetQtySold", "Qty sold (net)")} value={totals.netQtySold.toLocaleString()} iconBg="#dbeafe" />,
      <StatCard key="net-weight" icon={<ShopOutlined style={{ color: "#0d9488" }} />} label={t("vendorNetWeightSold", "Weight sold (net)")} value={`${totals.netWeightSold.toLocaleString()} ${t("kg", "kg")}`} iconBg="#ccfbf1" />,
      <StatCard key="gross" icon={<ShopOutlined style={{ color: "#0891b2" }} />} label={t("vendorGross", "Gross sold")} value={formatCurrency(totals.gross, mainCode)} iconBg="#cffafe" />,
      // Commission summary is intentionally hidden from the Vendors page.
      <StatCard key="remaining" icon={<DollarOutlined style={{ color: "#e11d48" }} />} label={t("vendorRemainingToClear", "Owed to farmers")} value={formatCurrency(totals.remaining, mainCode)} iconBg="#fee2e2" valueColor={T.red} />,
    ]
    : [
      <StatCard key="total" icon={<TeamOutlined style={{ color: "#2563eb" }} />} label={t("vendorsTitle", "Vendors")} value={rows.length} iconBg="#dbeafe" />,
      <StatCard key="net-qty" icon={<ShopOutlined style={{ color: "#2563eb" }} />} label={t("vendorNetQtySold", "Qty sold (net)")} value={totals.netQtySold.toLocaleString()} iconBg="#dbeafe" />,
      <StatCard key="net-weight" icon={<ShopOutlined style={{ color: "#0d9488" }} />} label={t("vendorNetWeightSold", "Weight sold (net)")} value={`${totals.netWeightSold.toLocaleString()} ${t("kg", "kg")}`} iconBg="#ccfbf1" />,
      <StatCard key="balance" icon={<DollarOutlined style={{ color: "#e11d48" }} />} label={t("vendorTotalOwed", "Total Owed")} value={formatCurrency(totals.remaining, mainCode)} iconBg="#fee2e2" valueColor={T.red} />,
      <StatCard key="debtors" icon={<ShopOutlined style={{ color: "#d97706" }} />} label={t("vendorsWithBalance", "With balance")} value={debtors} iconBg="#fef3c7" />,
    ];

  return (
    <>
      <ListPage
        breadcrumb={["Contacts", t("vendorsMenu", "Vendors")]}
        title={t("vendorsTitle", "Vendors")}
        subtitle={t("vendorsPageHint", "Expand a row to view purchase orders, dates, totals and an edit link.")}
        stats={vendorStats}
        toolbar={
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <Input.Search
              placeholder={t("searchHere", "Search here")}
              style={{ width: 280 }}
              allowClear
              onSearch={(v) => { setSearchText(v); resetPage(); }}
              onChange={(e) => { setSearchText(e.target.value); resetPage(); }}
            />
            <div style={{ flex: 1 }} />
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading} style={{ borderRadius: 9 }}>
              {t("currencyTotalsRefresh", "Refresh")}
            </Button>
            {canAdd && (
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} style={{ borderRadius: 9, fontWeight: 600 }}>
                {t("vendorsAdd", "New Vendor")}
              </Button>
            )}
          </div>
        }
      >
        <Table
          loading={loading}
          columns={columns}
          dataSource={filteredRows.map((r) => ({ ...r, key: r.id }))}
          pagination={pagination}
          scroll={{ x: canFinance ? 1050 : 700 }}
        />
      </ListPage>

      <Modal
        title={editing ? t("vendorsEditTitle", "Edit Vendor") : t("vendorsCreateTitle", "New Vendor")}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={submit}
        okText={t("purchaseOrdersSave", "Save")}
        width={480}
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label={t("purchaseOrdersVendorName", "Name")} rules={[{ required: true, message: t("vendorNameRequired", "Vendor name is required") }]}>
            <Input />
          </Form.Item>
          <Form.Item name="phone" label={t("purchaseOrdersVendorPhone", "Phone")}>
            <Input />
          </Form.Item>
          <Form.Item name="notes" label={t("vendorsNotes", "Notes")}>
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default Vendors;
